import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAppSelector } from '../store'
import Layout from '../components/common/Layout'
import AdminLayout from '../components/common/AdminLayout'

// Auth pages
import LoginPage from '../pages/auth/LoginPage'
import RegisterPage from '../pages/auth/RegisterPage'
import VerifyOtpPage from '../pages/auth/VerifyOtpPage'
import ForgotPasswordPage from '../pages/auth/ForgotPasswordPage'
import ResetPasswordPage from '../pages/auth/ResetPasswordPage'

// Landing page
import LandingPage from '../pages/LandingPage'

// Main pages
import FeedPage from '../pages/feed/FeedPage'
import JobDetailPage from '../pages/job/JobDetailPage'
import CreateJobPage from '../pages/job/CreateJobPage'
import MyJobsPage from '../pages/job/MyJobsPage'
import ProfilePage from '../pages/profile/ProfilePage'
import EditProfilePage from '../pages/profile/EditProfilePage'
import ConnectionsPage from '../pages/connections/ConnectionsPage'
import ChatPage from '../pages/chat/ChatPage'
import ConversationPage from '../pages/chat/ConversationPage'
import WalletPage from '../pages/wallet/WalletPage'
import MyApplicationsPage from '../pages/referrals/MyApplicationsPage'
import MyReferralsPage from '../pages/referrals/MyReferralsPage'
import ReferralDetailPage from '../pages/referrals/ReferralDetailPage'
import JobApplicationsPage from '../pages/job/JobApplicationsPage'
import NotificationsPage from '../pages/notifications/NotificationsPage'

// Legal pages
import PrivacyPolicyPage from '../pages/legal/PrivacyPolicyPage'

// Admin pages
import AdminDashboard from '../pages/admin/AdminDashboard'
import AdminJobsPage from '../pages/admin/AdminJobsPage'
import AdminUsersPage from '../pages/admin/AdminUsersPage'
import AdminSettingsPage from '../pages/admin/AdminSettingsPage'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAppSelector((s) => s.auth)
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAppSelector((s) => s.auth)
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (!user?.roles.includes('ADMIN') && !user?.roles.includes('SUPER_ADMIN')) {
    return <Navigate to="/" replace />
  }
  return <>{children}</>
}

function GuestRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAppSelector((s) => s.auth)
  return isAuthenticated ? <Navigate to="/" replace /> : <>{children}</>
}

export default function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Guest-only routes */}
        <Route path="/login"    element={<GuestRoute><LoginPage /></GuestRoute>} />
        <Route path="/register" element={<GuestRoute><RegisterPage /></GuestRoute>} />
        <Route path="/verify-otp"       element={<VerifyOtpPage />} />
        <Route path="/forgot-password"  element={<GuestRoute><ForgotPasswordPage /></GuestRoute>} />
        <Route path="/reset-password"   element={<GuestRoute><ResetPasswordPage /></GuestRoute>} />

        {/* Landing page — public, no backend calls */}
        <Route path="/" element={<LandingPage />} />

        {/* Public + Auth routes */}
        <Route element={<Layout />}>
          <Route path="/feed"      element={<FeedPage />} />
          <Route path="/jobs/:id"  element={<JobDetailPage />} />
          <Route path="/referral/:referralId" element={<ReferralDetailPage />} />

          {/* Protected routes */}
          <Route path="/create-job"               element={<ProtectedRoute><CreateJobPage /></ProtectedRoute>} />
          <Route path="/my-jobs"                  element={<ProtectedRoute><MyJobsPage /></ProtectedRoute>} />
          <Route path="/jobs/:id/applications"    element={<ProtectedRoute><JobApplicationsPage /></ProtectedRoute>} />
          <Route path="/profile"      element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/profile/edit" element={<ProtectedRoute><EditProfilePage /></ProtectedRoute>} />
          <Route path="/profile/:id"  element={<ProfilePage />} />
          <Route path="/connections"  element={<ProtectedRoute><ConnectionsPage /></ProtectedRoute>} />
          <Route path="/chat"                    element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
          <Route path="/chat/:conversationId"    element={<ProtectedRoute><ConversationPage /></ProtectedRoute>} />
          <Route path="/wallet"       element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
          <Route path="/my-applications" element={<ProtectedRoute><MyApplicationsPage /></ProtectedRoute>} />
          <Route path="/my-referrals"    element={<ProtectedRoute><MyReferralsPage /></ProtectedRoute>} />
          <Route path="/notifications"   element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
        </Route>

        {/* Admin routes */}
        <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
          <Route index          element={<AdminDashboard />} />
          <Route path="jobs"    element={<AdminJobsPage />} />
          <Route path="users"   element={<AdminUsersPage />} />
          <Route path="settings" element={<AdminSettingsPage />} />
        </Route>

        {/* Public legal pages */}
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}
